import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  ShieldCheck, Flag, Search, ClipboardCheck, LineChart, FileText, Palette, Globe, Megaphone, Rocket,
  Check, ArrowRight, Loader2, Bot, FileDown, Mail, Send,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { VeteranDiscountBanner } from "@/components/VeteranDiscountBanner";

const OUTCOMES = [
  "A validated business idea — built around your skills, your passion, or a market opportunity",
  "A comprehensive business plan",
  "A registered business with a professional brand",
  "A live, professionally designed website",
  "A Google Business Profile + social media presence",
  "Business email, marketing materials & QR codes",
  "An AI Business Strategist (Top Foster) in your corner",
  "A practical 12-month growth roadmap",
];

const WEEKS = [
  { n: "1", icon: Flag, title: "Identity", items: ["Who do I want to become after the military?", "Skills & interests", "Vision"] },
  { n: "2", icon: Search, title: "Business Discovery", items: ["Business Assessment", "Design preferences", "Military experience translation", "AI recommendations"] },
  { n: "3", icon: LineChart, title: "Business Validation", items: ["Market research", "Competitor analysis", "Pricing", "Customer avatar", "SWOT"] },
  { n: "4", icon: FileText, title: "Business Plan", items: ["Mission & vision", "Financials", "Marketing & funding", "Operations & growth"] },
  { n: "5", icon: Palette, title: "Brand Development", items: ["Logo", "Color palette", "Fonts", "Voice & tagline"] },
  { n: "6", icon: Globe, title: "Website", items: ["Launch website", "Contact forms & SEO", "Booking", "Lead capture"] },
  { n: "7", icon: Megaphone, title: "Marketing", items: ["Google Business Profile", "Facebook, Instagram, LinkedIn", "YouTube / TikTok", "Email marketing"] },
  { n: "8", icon: Rocket, title: "Launch Week", items: ["Website & socials live", "Business cards & QR codes", "Business email", "Analytics & CRM"] },
];

const PARTNERS = ["DoD Transition (TAP)", "Veteran Service Organizations", "Small Business Development Centers", "SCORE Mentors", "SBA Resource Partners", "Chambers of Commerce"];

const INPUT = "w-full bg-navy border border-white/15 text-white px-4 py-3 text-sm focus:border-gold focus:outline-none transition-colors";

export default function VeteransInitiative() {
  const [form, setForm] = useState({ name: "", email: "", audience: "service_member", organization: "", branch: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Military Skills Translator
  const [sf, setSf] = useState({ branch: "", mos: "", years: "", interests: "" });
  const [sfBusy, setSfBusy] = useState(false);
  const [sfDl, setSfDl] = useState(false);
  const [sfRes, setSfRes] = useState(null);
  const [sfEmailOpen, setSfEmailOpen] = useState(false);
  const [sfEmail, setSfEmail] = useState("");
  const [sfEmailBusy, setSfEmailBusy] = useState(false);
  const setS = (k, v) => setSf((f) => ({ ...f, [k]: v }));
  const emailIdeas = async () => {
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(sfEmail.trim())) { toast.error("Please enter a valid email address."); return; }
    setSfEmailBusy(true);
    try {
      await api.post("/skills-translator/email", { email: sfEmail.trim(), branch: sf.branch, mos: sf.mos, years: sf.years, skills: sfRes.skills || [], ideas: sfRes.ideas || [] });
      toast.success("Sent! Check your inbox for your ideas.");
      setSfEmailOpen(false); setSfEmail("");
    } catch (e) { toast.error("Could not send — please try again."); } finally { setSfEmailBusy(false); }
  };
  const downloadIdeas = async () => {
    setSfDl(true);
    try {
      const r = await api.post("/skills-translator/pdf",
        { branch: sf.branch, mos: sf.mos, years: sf.years, skills: sfRes.skills || [], ideas: sfRes.ideas || [] },
        { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = "my-business-ideas.pdf"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Could not create PDF — please try again."); } finally { setSfDl(false); }
  };
  const translate = async (e) => {
    e.preventDefault();
    if (!sf.mos.trim() && !sf.branch.trim() && !sf.interests.trim()) { toast.error("Add your branch, MOS or interests first."); return; }
    setSfBusy(true);
    try {
      const { data } = await api.post("/skills-translator", sf);
      setSfRes(data);
      if (!data.ok || !(data.ideas || []).length) toast.error("Couldn't generate ideas right now — please try again in a moment.");
    } catch (err) {
      toast.error(formatApiError(err) || "Something went wrong — please try again.");
    } finally { setSfBusy(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) { toast.error("Please add your name and email."); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) { toast.error("Please enter a valid email address."); return; }
    setBusy(true);
    try {
      await api.post("/veteran-interest", {
        name: form.name.trim(), email: form.email.trim(), audience: form.audience,
        organization: form.organization.trim(), branch: form.branch.trim(), message: form.message.trim(),
      });
      setDone(true);
      toast.success("Thank you for your interest — we'll be in touch.");
    } catch (err) {
      toast.error(formatApiError(err) || "Something went wrong — please try again.");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="veterans-page">
      {/* Hero */}
      <section className="relative border-b border-white/10 overflow-hidden grain">
        <div className="absolute -top-24 -right-24 w-96 h-96 bg-gold/10 rounded-full blur-3xl" />
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24 relative">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <a href="/veterans" className="text-gray-400 hover:text-gold text-xs mb-4 inline-block transition-colors" data-testid="trans-back-hub">← Veterans hub</a>
            <div className="flex items-center gap-2 text-gold text-xs uppercase tracking-[0.25em] mb-5">
              <ShieldCheck size={15} /> Transitioning Service Members · Operation Second Mission
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-light text-white leading-[1.05] max-w-4xl">
              Before you take off the uniform, <span className="text-gold">build your future.</span>
            </h1>
            <p className="text-base md:text-lg text-gray-300 font-light mt-6 max-w-2xl leading-relaxed">
              A structured program that equips transitioning service members to launch a real business before they leave active duty. Instead of walking away with only a DD-214, participants walk away with a business.
            </p>
            <div className="flex flex-wrap items-center gap-4 mt-8">
              <a href="#skills" data-testid="veterans-hero-cta" className="inline-flex items-center gap-2 bg-gold text-navy px-7 py-3.5 text-sm font-semibold hover:bg-gold-light transition-colors">
                Translate my military skills <ArrowRight size={16} />
              </a>
              <a href="/veterans/cohort" data-testid="veterans-partner-cta" className="inline-flex items-center gap-2 border border-white/20 text-white px-7 py-3.5 text-sm font-semibold hover:border-gold hover:text-gold transition-colors">
                Bring a cohort to your unit or org
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Discount */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 pt-12"><VeteranDiscountBanner /></section>

      {/* Military Skills Translator — moved high: the most interactive proof of value */}
      <section id="skills" className="max-w-7xl mx-auto px-6 md:px-12 py-16">
        <div className="flex items-center gap-2 text-gold text-xs uppercase tracking-[0.25em] mb-3"><Search size={15} /> Military Skills Translator</div>
        <h2 className="font-heading text-3xl text-white mb-3">Turn your MOS into business ideas</h2>
        <p className="text-gray-400 mb-8 max-w-2xl">Start right here. Tell us about your service and Top Foster will instantly translate your military experience into business ideas that fit your skills.</p>
        <div className="grid lg:grid-cols-2 gap-8 items-start">
          <form onSubmit={translate} className="border border-white/10 bg-navy p-6 space-y-4" data-testid="skills-form">
            <div className="grid sm:grid-cols-2 gap-4">
              <input className={INPUT} placeholder="Branch (e.g. Army)" value={sf.branch} onChange={(e) => setS("branch", e.target.value)} data-testid="skills-branch" />
              <input className={INPUT} placeholder="Years of service (e.g. 12)" value={sf.years} onChange={(e) => setS("years", e.target.value)} data-testid="skills-years" />
            </div>
            <input className={INPUT} placeholder="MOS / AFSC / Rating (e.g. 92A Logistics)" value={sf.mos} onChange={(e) => setS("mos", e.target.value)} data-testid="skills-mos" />
            <textarea className={`${INPUT} resize-none`} rows={3} placeholder="Interests or what you'd love to do (optional)" value={sf.interests} onChange={(e) => setS("interests", e.target.value)} data-testid="skills-interests" />
            <button type="submit" disabled={sfBusy} data-testid="skills-submit"
              className="w-full inline-flex items-center justify-center gap-2 bg-gold text-navy px-6 py-3.5 text-sm font-semibold hover:bg-gold-light transition-colors disabled:opacity-60">
              {sfBusy ? <Loader2 className="animate-spin" size={16} /> : <>Translate my experience <ArrowRight size={16} /></>}
            </button>
          </form>
          <div data-testid="skills-results">
            {!sfRes ? (
              <div className="border border-dashed border-white/15 p-8 text-center text-gray-500 text-sm h-full flex items-center justify-center min-h-[220px]">Your matched business ideas will appear here.</div>
            ) : (
              <div className="space-y-4">
                {(sfRes.skills || []).length > 0 && (
                  <div className="flex flex-wrap gap-2">{sfRes.skills.map((s) => <span key={s} className="bg-gold/15 text-gold border border-gold/30 px-3 py-1 text-xs">{s}</span>)}</div>
                )}
                {(sfRes.ideas || []).map((it, i) => (
                  <div key={i} className="border border-white/10 bg-navy p-5" data-testid={`skills-idea-${i}`}>
                    <h3 className="font-heading text-lg text-white mb-1.5">{it.title}</h3>
                    <p className="text-gray-400 text-sm mb-2">{it.why_fit}</p>
                    {it.first_step && <p className="text-sm text-gray-300"><span className="text-gold font-semibold">First step:</span> {it.first_step}</p>}
                  </div>
                ))}
                {(sfRes.ideas || []).length > 0 && (
                  <div className="space-y-3">
                    <div className="flex flex-wrap items-center gap-4">
                      <a href="/start" className="inline-flex items-center gap-2 text-gold text-sm font-semibold hover:gap-3 transition-all" data-testid="skills-start-cta">Start building one in the assessment <ArrowRight size={15} /></a>
                      <button onClick={downloadIdeas} disabled={sfDl} data-testid="skills-download-pdf"
                        className="inline-flex items-center gap-1.5 text-sm border border-white/15 text-white px-4 py-2 hover:border-gold/50 transition-colors disabled:opacity-60">
                        {sfDl ? <Loader2 className="animate-spin" size={14} /> : <FileDown size={14} />} Download my ideas (PDF)
                      </button>
                      <button onClick={() => setSfEmailOpen((v) => !v)} data-testid="skills-email-toggle"
                        className="inline-flex items-center gap-1.5 text-sm border border-white/15 text-white px-4 py-2 hover:border-gold/50 transition-colors">
                        <Mail size={14} /> Email it to me
                      </button>
                    </div>
                    {sfEmailOpen && (
                      <div className="flex items-center gap-2" data-testid="skills-email-form">
                        <input type="email" value={sfEmail} onChange={(e) => setSfEmail(e.target.value)} placeholder="you@email.com"
                          data-testid="skills-email-input" onKeyDown={(e) => e.key === "Enter" && emailIdeas()}
                          className="flex-1 bg-navy border border-white/20 text-white px-3 py-2 text-sm focus:border-gold focus:outline-none" />
                        <button onClick={emailIdeas} disabled={sfEmailBusy} data-testid="skills-email-send"
                          className="inline-flex items-center gap-1.5 bg-gold text-navy px-4 py-2 text-sm font-semibold hover:bg-gold-light transition-colors disabled:opacity-60">
                          {sfEmailBusy ? <Loader2 className="animate-spin" size={14} /> : <Send size={14} />} Send
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* AI does the work — moved high: explains the differentiator */}
      <section className="border-y border-white/10 bg-navy-800/40">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16">
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            <div>
              <div className="flex items-center gap-2 text-gold text-xs uppercase tracking-[0.25em] mb-4"><Bot size={15} /> Powered by AI</div>
              <h2 className="font-heading text-3xl text-white mb-4">Your AI strategist does most of the work</h2>
              <p className="text-gray-300 leading-relaxed mb-4">
                Instead of instructors doing everything, the platform guides each veteran individually — so one facilitator can support an entire cohort of 30.
              </p>
              <p className="text-gray-400 text-sm leading-relaxed">
                Top Foster translates military experience into business opportunities, guides planning and strategy, generates marketing and branding, assists with the website, and provides ongoing coaching after separation.
              </p>
            </div>
            <div className="border border-white/10 bg-navy-800 p-6 space-y-4">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center text-gray-300 text-xs shrink-0 mt-0.5">You</div>
                <p className="text-gray-200 text-sm bg-white/5 border border-white/10 px-4 py-3">"I was a logistics NCO for 12 years."</p>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 border border-gold/40 flex items-center justify-center text-gold shrink-0 mt-0.5"><Bot size={16} /></div>
                <p className="text-gray-200 text-sm bg-gold/10 border border-gold/25 px-4 py-3">
                  "Based on your experience, here are 10 businesses that fit your background. Choose one — now let's build it: assessment, plan, website and growth roadmap."
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Outcomes */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-16">
        <h2 className="font-heading text-3xl text-white mb-3">Leave with a business — not just a DD-214</h2>
        <p className="text-gray-400 mb-10 max-w-2xl">By the end of the program, every participant has:</p>
        <div className="grid sm:grid-cols-2 gap-x-8 gap-y-4">
          {OUTCOMES.map((o) => (
            <div key={o} className="flex items-start gap-3 text-gray-200"><Check size={18} className="text-gold shrink-0 mt-0.5" /> <span className="text-sm md:text-base">{o}</span></div>
          ))}
        </div>
      </section>

      {/* 8-week process */}
      <section className="border-y border-white/10 bg-navy-800/40">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16">
          <h2 className="font-heading text-3xl text-white mb-3">The 8-week launch process</h2>
          <p className="text-gray-400 mb-10 max-w-2xl">A proven, step-by-step framework — with the ATF WebWorks platform doing most of the heavy lifting.</p>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {WEEKS.map((w, i) => (
              <motion.div key={w.n} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: i * 0.05 }}
                className="border border-white/10 bg-navy p-6 flex flex-col" data-testid={`veterans-week-${w.n}`}>
                <div className="flex items-center justify-between mb-4">
                  <span className="text-gold font-heading text-3xl">Wk {w.n}</span>
                  <div className="w-10 h-10 border border-gold/40 flex items-center justify-center text-gold"><w.icon size={18} /></div>
                </div>
                <h3 className="font-heading text-lg text-white mb-3">{w.title}</h3>
                <ul className="space-y-1.5">
                  {w.items.map((it) => <li key={it} className="text-gray-400 text-sm flex items-start gap-2"><span className="text-gold mt-1.5 h-1 w-1 rounded-full bg-gold shrink-0" /> {it}</li>)}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Partners */}
      <section className="border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-12">
          <p className="text-center text-xs uppercase tracking-[0.25em] text-gray-500 mb-2">For transition offices, VSOs &amp; resource partners</p>
          <p className="text-center text-sm text-gray-400 max-w-2xl mx-auto mb-6">We partner with the organizations that support transitioning service members — to run cohorts on your installation or through your program.</p>
          <div className="flex justify-center mb-8">
            <a href="/veterans/cohort" data-testid="veterans-cohort-cta" className="inline-flex items-center gap-2 bg-gold text-navy px-6 py-3 text-sm font-semibold hover:bg-gold-light transition-colors">
              Request a cohort <ArrowRight size={15} />
            </a>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-3">
            {PARTNERS.map((p) => (
              <span key={p} className="border border-white/10 text-gray-300 text-sm px-4 py-2">{p}</span>
            ))}
          </div>
        </div>
      </section>

      {/* Founder + Interest form */}
      <section id="interest" className="max-w-7xl mx-auto px-6 md:px-12 py-16">
        <div className="grid lg:grid-cols-2 gap-10 items-start">
          <div>
            <div className="flex items-center gap-2 text-gold text-xs uppercase tracking-[0.25em] mb-4"><Flag size={15} /> Built by a veteran</div>
            <h2 className="font-heading text-3xl text-white mb-4">A mission worth serving</h2>
            <p className="text-gray-300 leading-relaxed mb-4">
              Founded by a retired service member with 26 years of active-duty experience, ATF WebWorks understands the transition firsthand. Our goal is simple: help veterans replace uncertainty with opportunity by turning military experience into successful business ownership — before they ever walk out the gate.
            </p>
            <p className="text-gray-400 text-sm leading-relaxed">
              Whether you're a transitioning service member or a transition office, VSO or resource partner, we'd love to talk about bringing a cohort to your installation or organization.
            </p>
          </div>

          {done ? (
            <div className="border border-gold/40 bg-gold/10 p-8 text-center" data-testid="veterans-form-success">
              <div className="w-14 h-14 border border-gold/40 rounded-full flex items-center justify-center text-gold mx-auto mb-4"><Check size={26} /></div>
              <h3 className="font-heading text-2xl text-white mb-2">Thank you for your interest</h3>
              <p className="text-gray-300 text-sm">We've received your information and will reach out soon with next steps.</p>
            </div>
          ) : (
            <form onSubmit={submit} className="border border-white/10 bg-navy-800 p-6 space-y-4" data-testid="veterans-interest-form">
              <h3 className="font-heading text-xl text-white">Request info / partner with us</h3>
              <div className="grid grid-cols-2 gap-2">
                <button type="button" onClick={() => set("audience", "service_member")} data-testid="veterans-aud-member"
                  className={`px-3 py-2.5 text-sm border transition-colors ${form.audience === "service_member" ? "bg-gold text-navy border-gold" : "border-white/15 text-gray-300 hover:border-gold/50"}`}>
                  Service member
                </button>
                <button type="button" onClick={() => set("audience", "partner")} data-testid="veterans-aud-partner"
                  className={`px-3 py-2.5 text-sm border transition-colors ${form.audience === "partner" ? "bg-gold text-navy border-gold" : "border-white/15 text-gray-300 hover:border-gold/50"}`}>
                  Partner / organization
                </button>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <input data-testid="veterans-name" className={INPUT} placeholder="Your name" value={form.name} onChange={(e) => set("name", e.target.value)} />
                <input data-testid="veterans-email" type="email" className={INPUT} placeholder="you@email.com" value={form.email} onChange={(e) => set("email", e.target.value)} />
              </div>
              {form.audience === "partner" ? (
                <input data-testid="veterans-org" className={INPUT} placeholder="Organization / installation" value={form.organization} onChange={(e) => set("organization", e.target.value)} />
              ) : (
                <input data-testid="veterans-branch" className={INPUT} placeholder="Branch & MOS/AFSC/Rating (optional)" value={form.branch} onChange={(e) => set("branch", e.target.value)} />
              )}
              <textarea data-testid="veterans-message" className={`${INPUT} resize-none`} rows={3} placeholder="Anything you'd like us to know? (optional)" value={form.message} onChange={(e) => set("message", e.target.value)} />
              <button type="submit" disabled={busy} data-testid="veterans-submit"
                className="w-full inline-flex items-center justify-center gap-2 bg-gold text-navy px-6 py-3.5 text-sm font-semibold hover:bg-gold-light transition-colors disabled:opacity-60">
                {busy ? <Loader2 className="animate-spin" size={16} /> : <>Send my request <ArrowRight size={16} /></>}
              </button>
              <p className="text-[11px] text-gray-500 text-center">We'll only use your details to follow up about the Veterans Launch Initiative.</p>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}
